import { useEffect, type ComponentType } from 'react'
import * as surfaceAppsModule from '@shell-surface-apps'
import { ClaudeHost } from '../../claude/ClaudePanel'
import { CommandPalette } from '../../components/CommandPalette'
import { Splash } from '../../components/Splash'
import { SurfaceHost } from '../../hosts/SurfaceHost'
import { activeInstance } from '../../instances'
import { productName } from '../../lib/config'
import { shellNavigate, usePathname } from '../../lib/navigation'
import { useShellStore } from '../../lib/store'
import { DEFAULT_SURFACE_PATH, surfaceForPathname, useVisibleSurfaces } from '../../surfaces'
import { TopBar } from './TopBar'
import './launcher.css'

// The launcher layout of the customer front door (instance frontDoor.layout
// === 'launcher'; Nateva first, Sam 16 Oct 2026: "it should feel like
// Nateva's central launcher, the place you sign in once and open the
// platforms you have access to"). No Command Centre side menu, no Search, no
// Focus: one slim top bar on every page of the front-door site and of every
// app build, so each platform feels like one product. Pages show; they do not
// change things.

// An instance on the People contract exports a "See it as" bar, shown under the top bar.
const PeopleBar = (surfaceAppsModule as Record<string, unknown>).PeopleBar as
  | ComponentType<{ surfaceId?: string }>
  | undefined

export function LauncherChrome() {
  const pathname = usePathname()
  const setPaletteOpen = useShellStore((s) => s.setPaletteOpen)
  const surface = surfaceForPathname(pathname)
  const visible = useVisibleSurfaces()

  // '/' (or a path this build does not carry) lands on the first tab.
  useEffect(() => {
    if (!surface) shellNavigate(DEFAULT_SURFACE_PATH)
  }, [surface, pathname])

  // Ctrl+K (Cmd+K) opens the search window from any page: the same window the Command Centre layout has, here
  // finding people, blocks, deals, agreements and notes across every app when the instance brings its own search.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen(true)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [setPaletteOpen])

  // Every build with more than one working tab shows them under the bar: an
  // app's own tabs, and on the front-door site Apps, Programme, Reports, Team
  // and Brand, so nobody has to find them in the person menu (Sam, 25 Oct 2026:
  // "I'm completely lost as to where the navigation has gone").
  // Using Claude lives in the person menu, so it is never a tab here.
  // Pages the instance keeps off the nav (navHidden, like Notifications behind the bell) are never tabs.
  const hidden: readonly string[] = activeInstance.navHidden ?? []
  const tabs = visible.filter((s) => s.meta.id !== 'learn' && !hidden.includes(s.meta.id))

  return (
    <div className="nl">
      <TopBar surface={surface} />
      {PeopleBar && <PeopleBar surfaceId={surface?.meta.id} />}
      {tabs.length > 1 && (
        <nav className="nl-tabs" aria-label="Pages">
          <div className="nl-wrap nl-tabs-in">
            {tabs.map((t) => (
              <button
                key={t.meta.id}
                type="button"
                className="nl-tab"
                aria-current={t.meta.id === surface?.meta.id ? 'page' : undefined}
                onClick={() => shellNavigate(t.meta.basePath)}
              >
                {t.meta.label}
              </button>
            ))}
          </div>
        </nav>
      )}
      <main className="nl-page">
        {surface ? <SurfaceHost key={surface.meta.id} surface={surface} /> : <Splash label={`Opening ${productName}…`} />}
      </main>
      <CommandPalette placeholder="Search every app: people, blocks, deals, agreements, notes" />
      <ClaudeHost
        surfaceId={surface?.meta.id}
        surfaceLabel={surface?.meta.label}
        screens={visible.filter((x) => x.meta.id !== 'learn').map((x) => x.meta.label)}
      />
    </div>
  )
}
